import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { Access } from "@/types/api";

const ALL_ACCESS_KEY = ["access", "all"];

export interface GrantAccessInput {
  client_id: string;
  mountpoint_id: string;
}

export interface ListAccessParams {
  client_id?: string;
  mountpoint_id?: string;
  include_revoked?: boolean;
}

export async function listAccess(params: ListAccessParams = {}): Promise<Access[]> {
  const query: Record<string, string> = {};
  if (params.client_id) query.client_id = params.client_id;
  if (params.mountpoint_id) query.mountpoint_id = params.mountpoint_id;
  if (params.include_revoked) query.include_revoked = "true";
  const { data } = await api.get<Access[]>("/api/access", { params: query });
  return data;
}

/**
 * Every active access row in one shot — lets the ACL page build a
 * client_id -> mountpoint_id -> access_id index without an N+1 fetch
 * per client/mountpoint row.
 */
export function useAllAccess() {
  return useQuery({
    queryKey: ALL_ACCESS_KEY,
    queryFn: () => listAccess(),
  });
}

export function useGrantAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: GrantAccessInput) =>
      (await api.post<Access>("/api/access", input)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ALL_ACCESS_KEY });
      qc.invalidateQueries({ queryKey: ["clients", vars.client_id, "mountpoints"] });
      qc.invalidateQueries({ queryKey: ["mountpoints", vars.mountpoint_id, "clients"] });
    },
  });
}

export function useRevokeAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (accessId: string) => {
      await api.delete(`/api/access/${accessId}`);
      return accessId;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ALL_ACCESS_KEY });
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["mountpoints"] });
    },
  });
}
